import React, {
  useCallback,
  useEffect,
  memo,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  ArrowLeftOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import { Virtuoso } from 'react-virtuoso';
import StepTimeline, { type StepStatus } from './StepTimeline';
import ProcessDiagramRenderer from './ProcessDiagramRenderer';
import OrganizationDiagramRenderer from './OrganizationDiagramRenderer';
import CandidatePool from './CandidatePool';
import { interpretEntry, type StreamAction } from './utils/streamInterpreter';
import type { ProcessGraphCompileResult } from './utils/processGraph';
import type { OrganizationGraphCompileResult } from './utils/organizationGraph';
import {
  ScenarioRuntimeEngine,
  clearSlotWorkingState,
  createProcessRuntimeState,
  ensureSlots,
  toggleSlotWorkingState,
  type ProcessResolveIntent,
} from './utils/runtimeEngine';
import {
  getOrganizationDetail,
  type OrganizationDetail,
} from '../../../../api';
import type {
  AssignedSlot,
  CandidatePerson,
  OrganizationRuntimeState,
  ProcessRuntimeState,
  RawStreamEntry,
  TaskPhase,
} from './types';
import type { RunConfig, LogEntry } from '../../index';
import RunConfigSummary from '../shared/RunConfigSummary';

/** 四个步骤的标签 */
const STEP_LABELS = [
  '创建过程分解结构',
  '招募数字员工',
  '过程角色匹配',
  '运行场景任务',
];
/** 全员点亮后，再额外停留 1 秒再进入第三步，确保第二步视觉完整。 */
const RECRUITING_COMPLETE_DELAY_MS = 1000;

interface Props {
  /** 用户提的问题 */
  question: string;
  /** 运行配置摘要 */
  config: RunConfig | null;
  /** 四个步骤的状态数组 */
  stepStatuses: StepStatus[];
  /** 运行日志列表 */
  logEntries: LogEntry[];
  /** 页面层队列是否暂停 */
  isPaused: boolean;
  suppressDiagramMotion?: boolean;
  /** 是否展示暂停控制与当前状态 */
  showPauseControl?: boolean;
  /** 暂停 / 继续页面层事件消费 */
  onTogglePause: () => void;
  onBackToList?: () => void;
  showViewResultLink?: boolean;
  onViewResult?: () => void;
  /** 第四步完成后，最终视口缩放动画结束通知父级。 */
  onDoneViewportSettled?: () => void;
}

/**
 * 运行过程页面组件。
 *
 * 布局：
 *   左侧：问题 + 运行配置
 *   中间（主视图）：步骤时间轴 + 过程图 / 组织架构图（按阶段切换）+ 候选人队伍
 *   右侧：运行日志
 *
 * 核心状态流：
 *   parent 传入 logEntries（每次新增一条），本组件用 processedLogIdxRef
 *   作为"进度指针"，逐条 interpretEntry 后更新四类状态：
 *     - phase（阶段）
 *     - processStates（过程图节点状态）
 *     - orgStates（组织图节点状态）
 *     - candidates（候选池成员）
 *   进入匹配/运行阶段时再根据各自事件进一步填充角色槽位 / 执行结果。
 */
const RunningProcess: React.FC<Props> = ({
  question,
  config,
  stepStatuses,
  logEntries,
  isPaused,
  suppressDiagramMotion = false,
  showPauseControl = true,
  onTogglePause,
  onBackToList,
  showViewResultLink = false,
  onViewResult,
  onDoneViewportSettled,
}) => {
  // ========================= 运行时核心状态 =========================
  const [phase, setPhase] = useState<TaskPhase>('structuring');
  const [processStates, setProcessStates] = useState<
    Map<string, ProcessRuntimeState>
  >(() => new Map());
  const [orgStates, setOrgStates] = useState<
    Map<string, OrganizationRuntimeState>
  >(() => new Map());
  const [candidates, setCandidates] = useState<CandidatePerson[]>([]);
  const [allAssigned, setAllAssigned] = useState(false);
  const [enteringCandidateIds, setEnteringCandidateIds] = useState<Set<string>>(
    () => new Set()
  );
  const [leavingCandidateIds, setLeavingCandidateIds] = useState<Set<string>>(
    () => new Set()
  );
  const [hiddenCandidateIds, setHiddenCandidateIds] = useState<Set<string>>(
    () => new Set()
  );
  const [showAssignmentComplete, setShowAssignmentComplete] = useState(false);

  // 通过 ref 维护"最新可读快照"，方便在批量消费日志时跨多次 dispatch 累积计算。
  // 每次 setState 时同步写入 ref，避免闭包拿到过期值。
  const processStatesRef = useRef(processStates);
  const candidatesRef = useRef(candidates);
  const orgStatesRef = useRef(orgStates);
  const phaseRef = useRef<TaskPhase>('structuring');
  const isPausedRef = useRef(isPaused);
  const hiddenCandidateIdsRef = useRef(hiddenCandidateIds);
  const leavingCandidateIdsRef = useRef(leavingCandidateIds);
  /** 第三步完成提示展示期间，仅延后 phase 切换，不再在组件内缓存动作。 */
  const pendingRunningPhaseRef = useRef(false);
  /** 第二步完成后，必须等所有员工点亮，再真正切第三步。 */
  const pendingMatchingPhaseRef = useRef(false);
  const pendingMatchingTimerRef = useRef<number | null>(null);
  useEffect(() => {
    processStatesRef.current = processStates;
  }, [processStates]);
  useEffect(() => {
    candidatesRef.current = candidates;
  }, [candidates]);
  useEffect(() => {
    orgStatesRef.current = orgStates;
  }, [orgStates]);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  useEffect(() => {
    hiddenCandidateIdsRef.current = hiddenCandidateIds;
  }, [hiddenCandidateIds]);
  useEffect(() => {
    leavingCandidateIdsRef.current = leavingCandidateIds;
  }, [leavingCandidateIds]);

  // 组织架构详情（第二步进入时请求）
  const [orgDetail, setOrgDetail] = useState<OrganizationDetail | null>(null);
  // 子图编译结果（持有 name->id 的查询索引）
  const processGraphRef = useRef<ProcessGraphCompileResult | null>(null);
  const orgGraphRef = useRef<OrganizationGraphCompileResult | null>(null);
  /** 运行过程数据引擎：负责消解同名节点与人员工作态。 */
  const runtimeEngineRef = useRef<ScenarioRuntimeEngine | null>(null);
  /** 组织图常常晚于日志到达，先按 name 暂存最新状态，等图编译好后再回放。 */
  const pendingOrgStatusRef = useRef<
    Map<string, OrganizationRuntimeState['status']>
  >(new Map());
  /** 组织详情加载失败时，不阻塞后续阶段，沿用原有降级行为。 */
  const orgDetailLoadFailedRef = useRef(false);
  /** 清理第二步待切第三步的等待定时器，避免旧任务串场。 */
  const cancelPendingMatchingPhase = useCallback(() => {
    pendingMatchingPhaseRef.current = false;
    if (pendingMatchingTimerRef.current !== null) {
      window.clearTimeout(pendingMatchingTimerRef.current);
      pendingMatchingTimerRef.current = null;
    }
  }, []);
  /** 后端日志已明确进入第三步后，直接切视图，避免因第二步乱序日志卡死在组织图。 */
  const forceEnterMatchingPhase = useCallback(() => {
    cancelPendingMatchingPhase();
    if (phaseRef.current === 'running' || phaseRef.current === 'done') return;
    setPhase('matching');
  }, [cancelPendingMatchingPhase]);
  /** 第二步“全部点亮”的判定：所有员工都已进入 approved / rejected 任一终态即可。 */
  const areAllRecruitingPeopleLit = useCallback(() => {
    if (orgDetailLoadFailedRef.current) return true;
    const graph = orgGraphRef.current;
    if (!graph) return false;
    return graph.personIds.every(id => {
      const status = orgStatesRef.current.get(id)?.status;
      return status === 'approved' || status === 'rejected';
    });
  }, []);
  /** 第二步收口统一走这里：全员点亮后再停留 1 秒进入第三步。 */
  const tryEnterMatchingAfterRecruitingComplete = useCallback(() => {
    if (!pendingMatchingPhaseRef.current) return;
    if (pendingMatchingTimerRef.current !== null) return;
    if (isPausedRef.current) return;
    if (!areAllRecruitingPeopleLit()) return;

    pendingMatchingTimerRef.current = window.setTimeout(() => {
      pendingMatchingTimerRef.current = null;
      if (!pendingMatchingPhaseRef.current) return;
      if (isPausedRef.current) return;
      if (phaseRef.current === 'running' || phaseRef.current === 'done') {
        pendingMatchingPhaseRef.current = false;
        return;
      }
      pendingMatchingPhaseRef.current = false;
      setPhase('matching');
    }, RECRUITING_COMPLETE_DELAY_MS);
  }, [areAllRecruitingPeopleLit]);

  // ========================= 日志消费指针 =========================
  /** 已经处理过的日志条数（只消费新条目） */
  const processedLogIdxRef = useRef(0);

  // 每次任务开始都重置；通过 config 的身份变化判定新一次运行。
  // 注意：这里不能把 processGraphRef / orgGraphRef 重置为 null —— 因为 ProcessDiagram
  // 的子 useEffect 会先执行并把最新图写入 ref，此处若再清空会覆盖掉新值。
  // 图本身随 processJson 变化重新编译并通过 onCompiled 覆盖，因此无需手动重置。
  useEffect(() => {
    processedLogIdxRef.current = 0;
    cancelPendingMatchingPhase();
    setPhase('structuring');
    const emptyProcess = new Map<string, ProcessRuntimeState>();
    const emptyOrg = new Map<string, OrganizationRuntimeState>();
    processStatesRef.current = emptyProcess;
    orgStatesRef.current = emptyOrg;
    candidatesRef.current = [];
    setProcessStates(emptyProcess);
    setOrgStates(emptyOrg);
    setCandidates([]);
    setAllAssigned(false);
    setShowAssignmentComplete(false);
    setLeavingCandidateIds(new Set());
    setHiddenCandidateIds(new Set());
    leavingCandidateIdsRef.current = new Set();
    hiddenCandidateIdsRef.current = new Set();
    pendingRunningPhaseRef.current = false;
    // ProcessDiagram 的编译回调可能先于本重置 effect 执行；这里不能直接清空引擎，
    // 否则日志消费入口会一直认为 graph 未就绪，导致第一步节点不再点亮。
    runtimeEngineRef.current = processGraphRef.current
      ? new ScenarioRuntimeEngine(processGraphRef.current)
      : null;
    setOrgDetail(null);
    pendingOrgStatusRef.current = new Map();
    orgDetailLoadFailedRef.current = false;
  }, [cancelPendingMatchingPhase, config]);

  useEffect(() => {
    return () => {
      cancelPendingMatchingPhase();
    };
  }, [cancelPendingMatchingPhase]);

  // ========================= 组织详情拉取 =========================
  // 进入招募阶段时请求单个组织详情（首次）
  useEffect(() => {
    if (phase !== 'recruiting') return;
    if (orgDetail) return;
    if (!config?.orgId) return;
    let cancelled = false;
    orgDetailLoadFailedRef.current = false;
    getOrganizationDetail(config.orgId)
      .then(detail => {
        if (cancelled) return;
        orgDetailLoadFailedRef.current = false;
        setOrgDetail(detail);
      })
      .catch(err => {
        if (cancelled) return;
        orgDetailLoadFailedRef.current = true;
        console.error('[RunningProcess] 获取组织详情失败', err);
        message.warning('获取组织详情失败，组织架构图将保持空白');
        tryEnterMatchingAfterRecruitingComplete();
      });
    return () => {
      cancelled = true;
    };
  }, [
    phase,
    config?.orgId,
    orgDetail,
    tryEnterMatchingAfterRecruitingComplete,
  ]);

  useEffect(() => {
    if (isPaused) return;
    tryEnterMatchingAfterRecruitingComplete();
  }, [isPaused, tryEnterMatchingAfterRecruitingComplete]);

  // ========================= 工具：更新过程节点状态 =========================
  /** 按唯一 processId 更新过程节点，避免同名节点被一并误更新。 */
  const updateProcessStateById = useCallback(
    (
      processId: string,
      updater: (prev: ProcessRuntimeState) => ProcessRuntimeState
    ) => {
      const graph = processGraphRef.current;
      if (!graph) return;
      const node = graph.processNodeMap.get(processId);
      const next = new Map(processStatesRef.current);
      const existing = createProcessRuntimeState(node, next.get(processId));
      next.set(processId, updater(existing));
      processStatesRef.current = next;
      setProcessStates(next);
    },
    []
  );

  const updateProcessStatesByIds = useCallback(
    (
      processIds: string[],
      updater: (
        prev: ProcessRuntimeState,
        processId: string
      ) => ProcessRuntimeState
    ) => {
      if (processIds.length === 0) return [] as string[];
      const graph = processGraphRef.current;
      if (!graph) return [] as string[];
      const next = new Map(processStatesRef.current);
      const updatedIds: string[] = [];
      processIds.forEach(processId => {
        const node = graph.processNodeMap.get(processId);
        if (!node) return;
        const existing = createProcessRuntimeState(node, next.get(processId));
        next.set(processId, updater(existing, processId));
        updatedIds.push(processId);
      });
      if (updatedIds.length === 0) return updatedIds;
      processStatesRef.current = next;
      setProcessStates(next);
      return updatedIds;
    },
    []
  );

  const getProcessIdsByName = useCallback((name: string) => {
    const graph = processGraphRef.current;
    if (!graph) {
      console.warn(
        '[RunningProcess] getProcessIdsByName: graph not ready, name=',
        name
      );
      return [] as string[];
    }
    const ids = graph.nameToProcessIds.get(name) || [];
    if (ids.length === 0) {
      console.warn(
        '[RunningProcess] getProcessIdsByName: no ids for name=',
        name,
        'available names=',
        Array.from(graph.nameToProcessIds.keys())
      );
    }
    return ids;
  }, []);

  /** 通过运行引擎把 name/processName 精确解析到唯一过程节点。 */
  const resolveProcessId = useCallback(
    (
      name: string,
      intent: ProcessResolveIntent,
      options: { personName?: string } = {}
    ) => {
      const graph = processGraphRef.current;
      const engine = runtimeEngineRef.current;
      if (!graph || !engine) {
        console.warn(
          '[RunningProcess] resolveProcessId: graph not ready, name=',
          name
        );
        return null;
      }
      const id = engine.resolveProcessId(
        name,
        intent,
        processStatesRef.current,
        options
      );
      if (!id) {
        console.warn(
          '[RunningProcess] resolveProcessId: no id for name=',
          name,
          'available names=',
          Array.from(graph.nameToProcessIds.keys())
        );
      }
      return id;
    },
    []
  );

  /** 按语义意图更新一个精确解析后的过程节点。 */
  const updateResolvedProcessState = useCallback(
    (
      name: string,
      intent: ProcessResolveIntent,
      updater: (
        prev: ProcessRuntimeState,
        processId: string
      ) => ProcessRuntimeState
    ) => {
      const processId = resolveProcessId(name, intent);
      if (!processId) return null;
      updateProcessStateById(processId, prev => updater(prev, processId));
      return processId;
    },
    [resolveProcessId, updateProcessStateById]
  );

  const updateMatchedProcessStates = useCallback(
    (
      name: string,
      updater: (
        prev: ProcessRuntimeState,
        processId: string
      ) => ProcessRuntimeState
    ) => {
      const processIds = getProcessIdsByName(name);
      if (processIds.length === 0) return [] as string[];
      return updateProcessStatesByIds(processIds, updater);
    },
    [getProcessIdsByName, updateProcessStatesByIds]
  );

  /** 人员开始/结束运行时，把对应角色卡片切换为工作中/空闲。 */
  const updatePersonWorkingState = useCallback(
    (processName: string, personName: string, isWorking: boolean) => {
      const processIds = getProcessIdsByName(processName);
      if (processIds.length === 0) return;

      updateProcessStatesByIds(processIds, (prev, processId) => {
        const graph = processGraphRef.current;
        const node = graph?.processNodeMap.get(processId);
        const slots = ensureSlots(node, prev.slots);
        return {
          ...prev,
          slots: toggleSlotWorkingState(slots, personName, isWorking),
        };
      });
    },
    [getProcessIdsByName, updateProcessStatesByIds]
  );

  /**
   * think 旧数据可能没有 processName，此时按“人员正在执行哪个节点”反推结果节点。
   *
   * 若同一人员并行执行多个节点，则结合结果文本关键词做二次判断，
   * 例如“重量”优先落到“获取零件重量范围”，“BOM”优先落到“获取零件BOM结构”。
   */
  const resolveResultProcessIds = useCallback(
    (
      processName: string | undefined,
      actorName: string | undefined,
      content: string
    ) => {
      if (processName) return getProcessIdsByName(processName);
      if (!actorName) return [] as string[];

      const graph = processGraphRef.current;
      if (!graph) return [] as string[];

      const workingProcessIds: string[] = [];
      processStatesRef.current.forEach((state, processId) => {
        const isActorWorking = state.slots.some(
          slot => slot.person?.name === actorName && slot.isWorking
        );
        if (isActorWorking) workingProcessIds.push(processId);
      });
      if (workingProcessIds.length === 0) return [] as string[];
      if (workingProcessIds.length === 1) return workingProcessIds;

      const scored = workingProcessIds
        .map((processId, index) => ({
          processId,
          index,
          score: scoreThinkResultTarget(
            graph.processNodeMap.get(processId)?.processName || '',
            content
          ),
        }))
        .sort((a, b) => b.score - a.score || b.index - a.index);

      const targetId = scored[0]?.processId;
      if (!targetId) return [] as string[];
      const targetName = graph.processNodeMap.get(targetId)?.processName;
      if (!targetName) return [targetId];
      return workingProcessIds.filter(
        processId =>
          graph.processNodeMap.get(processId)?.processName === targetName
      );
    },
    [getProcessIdsByName]
  );

  /** 将 think 内容写入对应结果节点，支持 processName 直连和 actorName 反推。 */
  const updateProcessResult = useCallback(
    (
      processName: string | undefined,
      actorName: string | undefined,
      content: string
    ) => {
      const processIds = resolveResultProcessIds(
        processName,
        actorName,
        content
      );
      if (processIds.length === 0) {
        console.warn(
          '[RunningProcess] processResult: cannot resolve target, processName=',
          processName,
          'actorName=',
          actorName
        );
        return;
      }

      updateProcessStatesByIds(processIds, prev => ({
        ...prev,
        result: joinThinkContent(content),
      }));
    },
    [resolveResultProcessIds, updateProcessStatesByIds]
  );

  // ========================= 工具：角色匹配 =========================
  /** 第三步匹配成功后，让候选池卡片先淡出，再从候选池展示中移除。 */
  const markCandidatesLeaving = useCallback((candidateIds: string[]) => {
    const ids = candidateIds.filter(
      id =>
        !hiddenCandidateIdsRef.current.has(id) &&
        !leavingCandidateIdsRef.current.has(id)
    );
    if (ids.length === 0) return;

    ids.forEach((id, index) => {
      const delay = index * 180;
      window.setTimeout(() => {
        setLeavingCandidateIds(prev => {
          const next = new Set(prev);
          next.add(id);
          leavingCandidateIdsRef.current = next;
          return next;
        });

        window.setTimeout(() => {
          setLeavingCandidateIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            leavingCandidateIdsRef.current = next;
            return next;
          });
          setHiddenCandidateIds(prev => {
            const next = new Set(prev);
            next.add(id);
            hiddenCandidateIdsRef.current = next;
            return next;
          });
        }, 360);
      }, delay);
    });
  }, []);

  /**
   * 从候选池挑选符合 role 的人员并挂到 processName 对应节点的 slot 上。
   *
   * 实现要点：
   * - 先由运行引擎消解同名节点，避免“修改稿件”等重复过程被一并填充。
   * - 候选池可以被多个过程节点复用（一个人可匹配多个过程），assignedTo 累加记录。
   */
  const assignRolesToProcess = useCallback(
    (processName: string, roles: string[]) => {
      const graph = processGraphRef.current;
      if (!graph || roles.length === 0) return;
      const processId = resolveProcessId(processName, 'assignRoles');
      if (!processId) return;

      // 基于最新 ref 快照计算（覆盖同一批次内的先前更新）
      const candidatesSnapshot = [...candidatesRef.current];
      const matchedCandidateIds = new Set<string>();
      const node = graph.processNodeMap.get(processId);
      if (!node || !node.isLeaf) return;
      const existing = createProcessRuntimeState(
        node,
        processStatesRef.current.get(processId)
      );
      const slots: AssignedSlot[] = ensureSlots(node, existing.slots);

      for (const role of roles) {
        const slotIdx = slots.findIndex(s => s.role === role && !s.person);
        if (slotIdx === -1) continue;
        const candIdx = candidatesSnapshot.findIndex(c =>
          c.roles.includes(role)
        );
        if (candIdx === -1) continue;
        const person = candidatesSnapshot[candIdx];
        slots[slotIdx] = { role, person };
        matchedCandidateIds.add(person.id);
        candidatesSnapshot[candIdx] = {
          ...person,
          assignedTo: [...person.assignedTo, processId],
        };
      }

      // 同步更新 ref，确保同一批次后续逻辑能读到最新值
      candidatesRef.current = candidatesSnapshot;
      const newProcessStates = new Map(processStatesRef.current);
      newProcessStates.set(processId, {
        ...existing,
        slots,
      });
      processStatesRef.current = newProcessStates;

      // 触发 React 更新
      setCandidates(candidatesSnapshot);
      setProcessStates(newProcessStates);
      markCandidatesLeaving(Array.from(matchedCandidateIds));
    },
    [markCandidatesLeaving, resolveProcessId]
  );

  /**
   * 将暂存的组织节点状态批量回放到实际图节点。
   *
   * 这样即使第二步日志先到、组织详情和 cytoscape 图稍后才准备好，也不会丢失点亮。
   */
  const applyPendingOrgStatuses = useCallback(() => {
    const graph = orgGraphRef.current;
    if (!graph) return;

    const nextOrgStates = new Map<string, OrganizationRuntimeState>();
    const nextCandidates = [...candidatesRef.current];
    const existingCandidateIds = new Set(nextCandidates.map(c => c.id));
    const enteringIds: string[] = [];

    pendingOrgStatusRef.current.forEach((status, name) => {
      const ids = graph.nameToIds.get(name);
      if (!ids || ids.length === 0) return;

      for (const id of ids) {
        nextOrgStates.set(id, { status });

        if (status !== 'approved' || existingCandidateIds.has(id)) continue;
        const node = graph.nodeMap.get(id);
        if (!node || node.kind !== 'person') continue;
        nextCandidates.push({
          id,
          // 候选池姓名只展示人员原名，角色统一放在第二行展示，避免 label 中的换行被 DOM 折叠成同一行。
          name: node.originalName,
          roles: node.roles,
          avatarUrl: node.avatarUrl,
          themeColor: node.themeColor,
          assignedTo: [],
        });
        existingCandidateIds.add(id);
        enteringIds.push(id);
      }
    });

    orgStatesRef.current = nextOrgStates;
    setOrgStates(nextOrgStates);
    candidatesRef.current = nextCandidates;
    setCandidates(nextCandidates);

    if (enteringIds.length > 0) {
      setEnteringCandidateIds(prev => {
        const set = new Set(prev);
        enteringIds.forEach(id => set.add(id));
        return set;
      });
      window.setTimeout(() => {
        setEnteringCandidateIds(prev => {
          const set = new Set(prev);
          enteringIds.forEach(id => set.delete(id));
          return set;
        });
      }, 40);
    }
    tryEnterMatchingAfterRecruitingComplete();
  }, [tryEnterMatchingAfterRecruitingComplete]);

  // ========================= 日志消费主入口 =========================
  useEffect(() => {
    if (logEntries.length <= processedLogIdxRef.current) return;
    const graphReady = !!processGraphRef.current && !!runtimeEngineRef.current;
    // 过程图还没编译完（processJson 未到），等待下一轮
    if (!graphReady) return;

    const newEntries = logEntries.slice(processedLogIdxRef.current);

    for (const raw of newEntries as RawStreamEntry[]) {
      const actions = interpretEntry(raw);
      /**
       * 第二步视觉兜底期间，允许继续消费组织图点亮日志，
       * 但必须阻塞第三/四步动作，避免底层过程图先被匹配/运行，
       * 等主视图真正切到第三步后，再继续消费后续日志。
       */
      if (
        pendingMatchingPhaseRef.current &&
        phaseRef.current === 'recruiting' &&
        shouldDeferEntryUntilMatching(actions)
      ) {
        // 说明后端已经开始往第三/四步推日志；此时优先尊重阶段推进，
        // 不再因为第二步个别乱序人员日志卡死在组织图。
        forceEnterMatchingPhase();
        break;
      }
      for (const action of actions) {
        dispatchAction(action);
      }
      processedLogIdxRef.current += 1;
    }

    /** 把一个语义化动作应用到具体状态。 */
    function dispatchAction(action: StreamAction) {
      // “完成全部组队” 已由页面队列统一做 post-delay，这里只在下一条真实运行事件到来时
      // 再切换到第四步，避免第三步完成提示与第四步执行动画同时出现。
      if (
        pendingRunningPhaseRef.current &&
        action.kind !== 'phase' &&
        action.kind !== 'done'
      ) {
        pendingRunningPhaseRef.current = false;
        setShowAssignmentComplete(false);
        setPhase('running');
      }

      switch (action.kind) {
        case 'phase':
          if (action.phase === 'matching') {
            setAllAssigned(false);
            setShowAssignmentComplete(false);
            pendingRunningPhaseRef.current = false;
            pendingMatchingPhaseRef.current = true;
            tryEnterMatchingAfterRecruitingComplete();
            // 进入匹配阶段：将所有叶子节点的 slots 初始化为"待派发"（保留已有 slot 内容）
            const graph = processGraphRef.current;
            if (graph) {
              const next = new Map(processStatesRef.current);
              graph.processNodeMap.forEach(node => {
                if (!node.isLeaf) return;
                const current = next.get(node.id);
                const existing = createProcessRuntimeState(node, current);
                if (
                  node.agents.length > 0 &&
                  (!current || current.slots.length !== existing.slots.length)
                ) {
                  next.set(node.id, {
                    ...existing,
                    status:
                      existing.status === 'pending'
                        ? 'structured'
                        : existing.status,
                  });
                }
              });
              processStatesRef.current = next;
              setProcessStates(next);
            }
          }
          if (action.phase === 'running') {
            cancelPendingMatchingPhase();
            if (action.source === 'groupingComplete') {
              // 真正收到“完成全部组队”后先展示完成提示，第四步 phase 等下一条运行事件再切换。
              setAllAssigned(true);
              setShowAssignmentComplete(true);
              pendingRunningPhaseRef.current = true;
            } else {
              // 后端显式发“开始执行过程”时，直接切到第四步；
              // 也兼容无招募/无匹配、第一步后直接进入运行阶段的流程。
              pendingRunningPhaseRef.current = false;
              setAllAssigned(true);
              setShowAssignmentComplete(false);
              setPhase('running');
            }
          }
          if (action.phase !== 'matching' && action.phase !== 'running') {
            cancelPendingMatchingPhase();
            pendingRunningPhaseRef.current = false;
            setShowAssignmentComplete(false);
            setPhase(action.phase);
          }
          break;

        case 'processStatus':
          if (action.status === 'running') {
            // 兼容旧日志：即使缺少“开始执行过程”，只要已经出现真实执行日志，也应进入第四步。
            cancelPendingMatchingPhase();
            pendingRunningPhaseRef.current = false;
            setShowAssignmentComplete(false);
            setPhase('running');
          }
          if (
            action.status === 'running' ||
            action.status === 'completed' ||
            action.status === 'destroyed'
          ) {
            updateMatchedProcessStates(action.name, prev => ({
              ...prev,
              status: action.status,
              slots:
                action.status === 'completed' || action.status === 'destroyed'
                  ? clearSlotWorkingState(prev.slots)
                  : prev.slots,
            }));
          } else {
            updateResolvedProcessState(
              action.name,
              resolveIntentByStatus(action.status),
              prev => ({
                ...prev,
                status: action.status,
                slots:
                  action.status === 'completed' || action.status === 'destroyed'
                    ? clearSlotWorkingState(prev.slots)
                    : prev.slots,
              })
            );
          }
          break;

        case 'processRoles': {
          // 把选中的角色落到 slots 上（第三步：从候选池中挑人匹配）
          assignRolesToProcess(action.name, action.roles);
          break;
        }

        case 'orgStatus':
          pendingOrgStatusRef.current.set(action.name, action.status);
          applyPendingOrgStatuses();
          break;

        case 'processResult':
          cancelPendingMatchingPhase();
          pendingRunningPhaseRef.current = false;
          setShowAssignmentComplete(false);
          setPhase('running');
          updateProcessResult(action.name, action.actorName, action.content);
          break;

        case 'personWorkStatus':
          cancelPendingMatchingPhase();
          pendingRunningPhaseRef.current = false;
          setShowAssignmentComplete(false);
          setPhase('running');
          updatePersonWorkingState(
            action.processName,
            action.personName,
            action.isWorking
          );
          break;

        case 'done': {
          cancelPendingMatchingPhase();
          pendingRunningPhaseRef.current = false;
          setShowAssignmentComplete(false);
          setPhase('done');
          // 后端可能因节点出错而提前发送 complete，未必有对应的 "结束运行" 事件来清掉工作态。
          // 这里兜底把所有 slot 的 isWorking 清掉，避免"工作中"胶囊和闪烁绿点继续转动；
          // 出错节点本身的 status='running' 保留，呼吸圈仍可指示流程最终停在哪一步。
          const cleared = new Map(processStatesRef.current);
          let mutated = false;
          cleared.forEach((state, processId) => {
            if (state.slots.some(s => s.isWorking)) {
              cleared.set(processId, {
                ...state,
                slots: clearSlotWorkingState(state.slots),
              });
              mutated = true;
            }
          });
          if (mutated) {
            processStatesRef.current = cleared;
            setProcessStates(cleared);
          }
          break;
        }

        case 'error':
          message.error(action.message);
          break;
      }
    }

    function shouldDeferEntryUntilMatching(actions: StreamAction[]) {
      return actions.some(action => {
        if (action.kind === 'error') return false;
        if (action.kind === 'orgStatus') return false;
        if (action.kind === 'phase' && action.phase === 'matching')
          return false;
        return true;
      });
    }
  }, [
    logEntries,
    phase,
    applyPendingOrgStatuses,
    assignRolesToProcess,
    cancelPendingMatchingPhase,
    forceEnterMatchingPhase,
    tryEnterMatchingAfterRecruitingComplete,
    updatePersonWorkingState,
    updateProcessResult,
    updateMatchedProcessStates,
    updateResolvedProcessState,
  ]);

  // ========================= 图编译回调 =========================
  const handleProcessCompiled = useCallback(
    (graph: ProcessGraphCompileResult) => {
      processGraphRef.current = graph;
      runtimeEngineRef.current = new ScenarioRuntimeEngine(graph);
    },
    []
  );
  const handleOrgCompiled = useCallback(
    (graph: OrganizationGraphCompileResult) => {
      orgGraphRef.current = graph;
      applyPendingOrgStatuses();
      tryEnterMatchingAfterRecruitingComplete();
    },
    [applyPendingOrgStatuses, tryEnterMatchingAfterRecruitingComplete]
  );

  // 构建步骤数据（优先使用 parent 传入的 stepStatuses，保证与 parent 同步）
  const steps = useMemo(
    () =>
      STEP_LABELS.map((label, i) => ({
        label,
        status: stepStatuses[i] || ('pending' as StepStatus),
      })),
    [stepStatuses]
  );

  // 是否展示组织图
  const showOrgDiagram = phase === 'recruiting';
  /** 候选池展示列表：第三步已匹配完成的候选人会逐个从这里移除。 */
  const visibleCandidates = useMemo(
    () => candidates.filter(candidate => !hiddenCandidateIds.has(candidate.id)),
    [candidates, hiddenCandidateIds]
  );

  // 是否展示候选池（第二、三步展示；进入第四步后仅短暂展示完成提示）
  const showCandidatePool =
    phase === 'recruiting' || phase === 'matching' || showAssignmentComplete;
  /** 候选人被提前装配完但组队未完成时，不显示完成，改为持续分配中的过渡态。 */
  const candidatePoolEmptyText =
    phase === 'matching' ? '人员持续分配中...' : '等待招募结果...';

  return (
    <div className="flex h-full min-h-0">
      {/* ========== 左侧：问题 + 运行配置摘要 ========== */}
      <div className="thin-scrollbar w-[240px] shrink-0 overflow-y-auto border-0 border-r border-solid border-[#d1d5db] bg-white px-5 py-5">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2094f0] text-white">
            <UserOutlined style={{ fontSize: 14 }} />
          </div>
          <span className="text-sm font-medium text-[#111827]">User Name</span>
        </div>
        {/* 用户问题最多展示 20 行，超出省略并用原生 title 提供完整内容 hover 查看。 */}
        <p
          className="mb-5 overflow-hidden text-sm leading-relaxed text-[#2094f0]"
          title={question}
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 20,
          }}
        >
          {question}
        </p>
        {config?.createdAt && (
          <p className="mb-5 text-sm text-[#98a2b3]">{config.createdAt}</p>
        )}
        {config && <RunConfigSummary config={config} />}
        {showViewResultLink && (
          <button
            type="button"
            onClick={onViewResult}
            className="mt-4 cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-[#2094f0] transition-colors hover:text-[#1a7fd4]"
          >
            查看运行结果
          </button>
        )}
      </div>

      {/* ========== 中间：步骤时间轴 + 主图 + 候选池 ========== */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="relative shrink-0 border-b border-[#e5e7eb] bg-white px-20 py-5">
          {onBackToList && (
            <div className="pointer-events-none absolute inset-y-0 left-8 top-[-4px] z-20 flex items-center">
              <button
                type="button"
                onClick={onBackToList}
                className="pointer-events-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-[#d0d5dd] bg-white text-[#344054] transition-colors hover:border-[#98a2b3] hover:text-[#101828]"
                aria-label="返回任务列表"
              >
                <ArrowLeftOutlined />
              </button>
            </div>
          )}
          <StepTimeline steps={steps} isPaused={isPaused} />
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50">
          {/* 主图区：用绝对叠加让切换更平滑，同时 cytoscape 实例不频繁销毁 */}
          <div className="relative min-h-0 flex-1">
            {/* 右上角浮层：复用主图区公共容器，避免分别侵入过程图/组织图组件。 */}
            {showPauseControl && (
              <div className="pointer-events-none absolute top-3 right-[110px] z-20">
                <div className="pointer-events-auto flex items-center gap-2 rounded-md bg-white/90 px-3 py-1.5 text-[11px] text-slate-500">
                  <span>当前状态：{isPaused ? '已暂停' : '运行中'}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={onTogglePause}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onTogglePause();
                      }
                    }}
                    title={isPaused ? '继续运行' : '暂停运行'}
                    aria-label={isPaused ? '继续运行' : '暂停运行'}
                    className="inline-flex cursor-pointer items-center text-[#4b5563] transition-colors hover:text-[#111827]"
                  >
                    {isPaused ? (
                      <PlayCircleOutlined style={{ fontSize: 16 }} />
                    ) : (
                      <PauseCircleOutlined style={{ fontSize: 16 }} />
                    )}
                  </span>
                </div>
              </div>
            )}
            {/* 过程图：除招募阶段外一直展示 */}
            <div
              className="absolute inset-0 transition-opacity duration-500"
              style={{
                opacity: showOrgDiagram ? 0 : 1,
                pointerEvents: showOrgDiagram ? 'none' : 'auto',
              }}
            >
              {config?.processJson && (
                <ProcessDiagramRenderer
                  processJson={config.processJson}
                  phase={phase}
                  processStates={processStates}
                  suppressMotion={suppressDiagramMotion}
                  onCompiled={handleProcessCompiled}
                  onDoneViewportSettled={onDoneViewportSettled}
                />
              )}
            </div>
            {/* 组织图：仅在招募阶段展示 */}
            <div
              className="absolute inset-0 transition-opacity duration-500"
              style={{
                opacity: showOrgDiagram ? 1 : 0,
                pointerEvents: showOrgDiagram ? 'auto' : 'none',
              }}
            >
              <OrganizationDiagramRenderer
                detail={orgDetail}
                orgStates={orgStates}
                suppressMotion={suppressDiagramMotion}
                onCompiled={handleOrgCompiled}
                mode="runtime"
              />
            </div>
          </div>

          {/* 候选池：第二步展示招募结果，第三步逐个消失并提示任务分配完成。 */}
          {showCandidatePool && (
            <CandidatePool
              candidates={visibleCandidates}
              allAssigned={allAssigned && showAssignmentComplete}
              emptyText={candidatePoolEmptyText}
              enteringIds={enteringCandidateIds}
              leavingIds={leavingCandidateIds}
            />
          )}
        </div>
      </div>

      <LogPanel logEntries={logEntries} />
    </div>
  );
};

export default RunningProcess;

const LOG_LIST_COMPONENTS = {
  EmptyPlaceholder: () => (
    <p className="px-4 py-3 text-xs text-[#9ca3af]">等待日志输出...</p>
  ),
};

const LogRow = memo(function LogRow({ entry }: { entry: LogEntry }) {
  const typeLabel = formatLogType(entry.type);
  return (
    <div className="border-b border-[#f3f4f6] px-4 py-2 last:border-b-0">
      <p className="mb-0 text-xs leading-relaxed">
        {/* <span className="mr-1 inline-flex rounded bg-[#f3f4f6] px-1.5 py-[1px] text-[10px] text-[#6b7280]">
          {typeLabel}
        </span> */}
        <span className="font-medium text-[#d97706]">[{entry.name}]</span>
        <span className="ml-1 text-[#111827]">{entry.content}</span>
      </p>
      {entry.processName && (
        <p className="mt-1 mb-0 text-[11px] leading-none text-[#94a3b8]">
          过程节点：{entry.processName}
        </p>
      )}
      <p className="mt-1 mb-0 text-[11px] leading-none text-[#9ca3af]">
        {entry.timestamp || '--:--:--'}
      </p>
    </div>
  );
});

const LogPanel = memo(function LogPanel({
  logEntries,
}: {
  logEntries: LogEntry[];
}) {
  /** 倒序展示：最新日志固定出现在最前面，避免依赖自动滚动。 */
  const reversedLogEntries = useMemo(
    () => [...logEntries].reverse(),
    [logEntries]
  );

  return (
    <div
      className="flex w-[300px] shrink-0 flex-col border-0 border-l border-solid border-[#d1d5db] bg-white"
      style={{ contain: 'layout paint' }}
    >
      <div className="shrink-0 border-b border-[#e5e7eb] px-4 py-3 pb-0 mb-0">
        <h4 className="text-sm font-bold text-[#111827] mb-0">运行日志</h4>
      </div>
      <div className="min-h-0 flex-1">
        <Virtuoso
          className="h-full thin-scrollbar"
          data={reversedLogEntries}
          components={LOG_LIST_COMPONENTS}
          increaseViewportBy={{ top: 240, bottom: 480 }}
          computeItemKey={(index, entry) =>
            `${entry.timestamp || 'no-time'}-${entry.name}-${index}`
          }
          followOutput={false}
          itemContent={(_, entry) => <LogRow entry={entry} />}
        />
      </div>
    </div>
  );
});

/**
 * think 事件的 content 经常是一个 JSON 字符串数组（如资料列表）。
 * 我们尝试解析后拼接成一句话；解析失败就原样展示。
 */
function joinThinkContent(raw: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  // 形如 "[\"x\",\"y\"]"
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const arr = JSON.parse(trimmed);
      if (Array.isArray(arr)) {
        return arr.map(x => String(x)).join('；');
      }
    } catch {
      /* ignore */
    }
  }
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const obj = JSON.parse(trimmed);
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        return Object.entries(obj)
          .map(([key, value]) => `${key}：${formatThinkValue(value)}`)
          .join('；');
      }
    } catch {
      /* ignore */
    }
  }
  return trimmed;
}

/** 把 process 状态转成运行引擎的解析意图。 */
function resolveIntentByStatus(
  status: ProcessRuntimeState['status']
): ProcessResolveIntent {
  if (status === 'initialized') return 'initialize';
  if (status === 'structured') return 'structure';
  if (status === 'running') return 'runStart';
  if (status === 'completed') return 'runComplete';
  if (status === 'destroyed') return 'destroy';
  return 'structure';
}

/** think 结果可能是对象字段值，统一压成适合结果节点展示的文本。 */
function formatThinkValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(item => String(item)).join('、');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value ?? '');
}

/** 右侧日志面板展示更友好的事件类型文案。 */
function formatLogType(type: string): string {
  if (type === 'log') return '日志';
  if (type === 'think') return '思考';
  if (type === 'answer') return '结果';
  if (type === 'complete') return '完成';
  if (type === 'error') return '错误';
  return type || '事件';
}

/** 根据 think 文本和过程名做轻量关键词匹配，解决同一人员并行工作时的结果归属。 */
function scoreThinkResultTarget(processName: string, content: string): number {
  const text = `${processName}\n${content}`;
  let score = 0;

  if (processName && content.includes(processName)) score += 8;
  if (processName.includes('重量') && /重量|最小|最大|公斤|kg/i.test(text)) {
    score += 6;
  }
  if (processName.includes('BOM') && /BOM|物料清单|结构树|组成/i.test(text)) {
    score += 6;
  }
  if (
    processName.includes('收集资料') &&
    /资料|列表|标题|报告|新闻/i.test(text)
  ) {
    score += 3;
  }
  if (processName.includes('评估资料') && /评估|有效|筛选|资料/i.test(text)) {
    score += 3;
  }
  if (processName.includes('编写') && /故事|标题|内容|文章/i.test(text)) {
    score += 3;
  }
  if (processName.includes('验收') && /建议|修改|验收|意见/i.test(text)) {
    score += 3;
  }
  if (processName.includes('审核') && /审核|意见|修改|通过/i.test(text)) {
    score += 3;
  }
  if (processName.includes('拟定标题') && /标题|题目/i.test(text)) {
    score += 3;
  }

  return score;
}
